import type { ReactNode } from "react";
import { cx } from "../lib/cx";

export interface FieldProps {
	label: ReactNode;
	/** id of the control inside — the label points at it. */
	htmlFor?: string;
	/** Helper text under the control. Replaced by `error` when one is set. */
	hint?: ReactNode;
	error?: ReactNode;
	required?: boolean;
	/** Inline note to the right of the label ("optional", "leave blank to generate"). */
	aside?: ReactNode;
	children: ReactNode;
	className?: string;
}

/**
 * Field — label / control / hint-or-error, in the one layout every form on
 * the platform uses. The control is whatever you pass (an input, a Select,
 * a checkbox row); the Field only owns the text around it.
 *
 * The designer's `.f`: 6px gaps, a 13.5px medium label in ink with a crit
 * star, 12.5px hint in ink-3, 12.5px medium error in bad.
 */
export function Field({ label, htmlFor, hint, error, required, aside, children, className }: FieldProps) {
	const errorId = htmlFor ? `${htmlFor}-error` : undefined;
	return (
		<div className={cx("flex min-w-0 flex-col gap-[0.375rem]", className)}>
			<div className="flex items-baseline justify-between gap-[0.5rem]">
				<label htmlFor={htmlFor} className="text-[0.84375rem] leading-[1.3] font-medium text-gms-navy-ink">
					{label}
					{required && (
						<span aria-hidden className="ml-[0.25rem] text-gms-crit">
							*
						</span>
					)}
				</label>
				{aside && <span className="text-[0.75rem] leading-none text-gms-ink-3">{aside}</span>}
			</div>
			{children}
			{error ? (
				<p id={errorId} role="alert" className="text-[0.78125rem] leading-[1.4] font-medium text-gms-bad">
					{error}
				</p>
			) : hint ? (
				<p className="text-[0.78125rem] leading-[1.4] text-gms-ink-3">{hint}</p>
			) : null}
		</div>
	);
}
